import {
  QualitySealAction,
  QualitySealProperties,
  QualitySealStatus,
  QualitySealStatusLabel,
} from './types';

/**
 * Number of days an entity remains in APPROVED status after last verification.
 *
 * @public
 */
export const APPROVED_DAYS = 90;

/**
 * Maximum number of days before an entity transitions from REVIEW_PENDING to UNVERIFIED.
 * Days 91-118 = REVIEW_PENDING, >= 119 = UNVERIFIED.
 *
 * @public
 */
export const REVIEW_PENDING_MAX_DAYS = 118;

/** Days an entity must remain unverified before entering the inactive state. */
export const INACTIVE_DAYS_AFTER_UNVERIFIED = 60;

/** Resolves and validates the configured inactivity threshold. */
export function resolveInactiveDaysAfterUnverified(
  configuredValue?: number,
): number {
  if (configuredValue === undefined) {
    return INACTIVE_DAYS_AFTER_UNVERIFIED;
  }

  if (!Number.isInteger(configuredValue) || configuredValue < 1) {
    throw new Error(
      'qualitySeal.inactiveDaysAfterUnverified must be a positive integer',
    );
  }

  return configuredValue;
}

export const UNVERIFIED_DAYS = REVIEW_PENDING_MAX_DAYS + 1;

/**
 * Computes the quality seal status based on the number of days since last verification.
 *
 * @param lastVerifiedAt - The date when the entity was last verified
 * @param now - The current date (defaults to new Date())
 * @returns The computed QualitySealStatus
 *
 * @public
 */
export function computeQualitySealStatus(
  lastVerifiedAt: Date,
  now: Date = new Date(),
  inactiveDaysAfterUnverified = INACTIVE_DAYS_AFTER_UNVERIFIED,
): QualitySealStatus {
  const diffMs = now.getTime() - lastVerifiedAt.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays <= APPROVED_DAYS) {
    return QualitySealStatus.APPROVED;
  }

  if (diffDays <= REVIEW_PENDING_MAX_DAYS) {
    return QualitySealStatus.REVIEW_PENDING;
  }

  if (diffDays >= UNVERIFIED_DAYS + inactiveDaysAfterUnverified) {
    return QualitySealStatus.INACTIVE;
  }

  return QualitySealStatus.UNVERIFIED;
}

/** Computes governance status for a manually forced Unverified entity. */
export function computeForcedUnverifiedStatus(
  unverifiedSince: Date,
  now: Date = new Date(),
  inactiveDaysAfterUnverified = INACTIVE_DAYS_AFTER_UNVERIFIED,
): QualitySealStatus.UNVERIFIED | QualitySealStatus.INACTIVE {
  const diffDays = Math.floor(
    (now.getTime() - unverifiedSince.getTime()) / (1000 * 60 * 60 * 24),
  );

  return diffDays >= inactiveDaysAfterUnverified
    ? QualitySealStatus.INACTIVE
    : QualitySealStatus.UNVERIFIED;
}

/**
 * Computes the effective status for a manually forced status. Forced
 * Unverified ages into Inactive from the time it was forced; every other
 * forced status is returned unchanged.
 */
export function computeForcedStatus(
  forcedStatus: QualitySealStatus,
  forcedAt: Date,
  now: Date = new Date(),
  inactiveDaysAfterUnverified = INACTIVE_DAYS_AFTER_UNVERIFIED,
): QualitySealStatus {
  return forcedStatus === QualitySealStatus.UNVERIFIED
    ? computeForcedUnverifiedStatus(forcedAt, now, inactiveDaysAfterUnverified)
    : forcedStatus;
}

/**
 * quality seal properties for colored representation everywhere
 */
export const qualitySealProperties: QualitySealProperties[] = [
  {
    value: QualitySealStatus.APPROVED,
    label: QualitySealStatusLabel.APPROVED,
    darkColor: '#2EFFAF',
    lightColor: '#00A868',
    description:
      'This entity was recently approved, and validated by the owner.',
    tooltip: 'This entity was recently approved, and validated by the owner.',
    actions: [
      {
        iconKey: 'RENEW',
        value: QualitySealStatus.APPROVED,
        label: 'Renew Approval',
        darkColor: '#2EFFAF',
        lightColor: '#00A868',
      },
      {
        iconKey: QualitySealStatus.UNVERIFIED,
        value: QualitySealStatus.UNVERIFIED,
        label: QualitySealStatusLabel.UNVERIFIED,
        darkColor: '#FE786E',
        lightColor: '#EE4C40',
      },
      {
        iconKey: QualitySealStatus.INACTIVE,
        value: QualitySealStatus.INACTIVE,
        label: QualitySealStatusLabel.INACTIVE,
        darkColor: 'var(--xlrt-color-ui-white)',
        lightColor: 'var(--xlrt-color-ui-gray20)',
        iconDarkColor: 'var(--xlrt-color-ui-gray75)',
        iconLightColor: 'var(--xlrt-color-ui-gray17)',
      },
    ],
  },
  {
    value: QualitySealStatus.REVIEW_PENDING,
    label: QualitySealStatusLabel.REVIEW_PENDING,
    darkColor: '#F4BF1F',
    lightColor: '#FF9800',
    description:
      'This entity is awaiting review from the owner and may change soon.',
    tooltip: 'This entity is waiting approval by its owner.',
    actions: [
      {
        iconKey: QualitySealStatus.APPROVED,
        value: QualitySealStatus.APPROVED,
        label: QualitySealStatusLabel.APPROVED,
        darkColor: '#2EFFAF',
        lightColor: '#00A868',
      },
      {
        iconKey: QualitySealStatus.UNVERIFIED,
        value: QualitySealStatus.UNVERIFIED,
        label: QualitySealStatusLabel.UNVERIFIED,
        darkColor: '#FE786E',
        lightColor: '#EE4C40',
      },
    ],
  },
  {
    value: QualitySealStatus.UNVERIFIED,
    label: QualitySealStatusLabel.UNVERIFIED,
    darkColor: '#FE786E',
    lightColor: '#EE4C40',
    description:
      "This entity wasn't updated since 119 days or longer and may be outdated.",
    tooltip: "This entity still hasn't been updated by its owner.",
    actions: [
      {
        iconKey: QualitySealStatus.APPROVED,
        value: QualitySealStatus.APPROVED,
        label: QualitySealStatusLabel.APPROVED,
        darkColor: '#2EFFAF',
        lightColor: '#00A868',
      },
      {
        iconKey: QualitySealStatus.INACTIVE,
        value: QualitySealStatus.INACTIVE,
        label: QualitySealStatusLabel.INACTIVE,
        darkColor: 'var(--xlrt-color-ui-white)',
        lightColor: 'var(--xlrt-color-ui-gray20)',
        iconDarkColor: 'var(--xlrt-color-ui-gray75)',
        iconLightColor: 'var(--xlrt-color-ui-gray17)',
      },
    ],
  },
  {
    value: QualitySealStatus.INACTIVE,
    label: QualitySealStatusLabel.INACTIVE,
    darkColor: 'var(--xlrt-color-ui-white)',
    lightColor: 'var(--xlrt-color-ui-gray20)',
    iconDarkColor: 'var(--xlrt-color-ui-gray75)',
    iconLightColor: 'var(--xlrt-color-ui-gray17)',
    description:
      'This entity is inactive: it was either marked inactive by its owner or stayed Unverified for too long without being updated or verified.',
    tooltip: 'This entity is inactive and requires governance review.',
    actions: [
      {
        iconKey: QualitySealStatus.UNVERIFIED,
        value: QualitySealStatus.UNVERIFIED,
        label: QualitySealStatusLabel.UNVERIFIED,
        darkColor: '#FE786E',
        lightColor: '#EE4C40',
      },
      {
        iconKey: QualitySealStatus.APPROVED,
        value: QualitySealStatus.APPROVED,
        label: QualitySealStatusLabel.APPROVED,
        darkColor: '#2EFFAF',
        lightColor: '#00A868',
      },
    ],
  },
];

/** Resolves the icon color of a quality seal status or action for the given theme. */
export function getQualitySealIconColor(
  seal: QualitySealProperties | QualitySealAction,
  isDark: boolean,
): string {
  return isDark
    ? (seal.iconDarkColor ?? seal.darkColor)
    : (seal.iconLightColor ?? seal.lightColor);
}

/**
 * quality seal properties map to get values based on status which is mapped as value
 */
export const qualitySealPropertiesMap = Object.fromEntries(
  qualitySealProperties.map(seal => [seal.value, seal]),
);

export const qualitySealNotAllowedEntities = [
  'domain',
  'template',
  'user',
  'location',
];

/** Day threshold for sending a reminder email. */
export const REMINDER_DAYS = 110;

export const NOTIFICATION_TOPIC = 'Notification about Quality Seal';

export const NOTIFICATION_TITLES = {
  REVIEW_PENDING: 'Action Required: Verify Your XELERATE Entities',
  REMINDER: 'Action Required: Reminder to Verify Your XELERATE Entities',
  UNVERIFIED: 'Critical: Your XELERATE Entities Are Now Unverified',
  INACTIVE: 'Quality Seal Status Changed: Entity Is Inactive',
} as const;

/**
 * `{ownerPhrase}` is filled from {@link NOTIFICATION_OWNER_PHRASES}: an entity
 * may be owned by a group or by an individual, and the sentence has to read
 * correctly either way.
 */
export const NOTIFICATION_INTROS = {
  REVIEW_PENDING:
    'One or more of the entities {ownerPhrase} have not been updated or verified since <strong>90 days</strong>. In order to keep the content in XELERATE fresh and helpful, please review if the information for the following entities is still up to date:',
  REMINDER:
    'one or more of the entities {ownerPhrase} have not been updated or verified since <strong>110 days</strong> and are still in the <strong>Review Pending</strong> state. In order to keep the content in XELERATE fresh and helpful, please review if the information for the following entities is still up to date:',
  UNVERIFIED:
    'one or more of the entities {ownerPhrase} have not been updated or verified since <strong>119 days</strong> and their <strong>Quality Seal has changed to Unverified</strong>. In order to keep the content in XELERATE fresh and helpful, please review and update the information for the following entities:',
  UNVERIFIED_MANUAL:
    'one or more of the entities {ownerPhrase} have been manually set to <strong>Unverified</strong>. Please review and update the information for the following entities:',
  INACTIVE:
    'one or more of the entities {ownerPhrase} have not been updated or verified for <strong>{inactiveDays} days</strong> and their <strong>Quality Seal has changed to Inactive</strong>. They are hidden from the Software Catalog by default and may be deleted after the applicable retention period. Please review and update these entities, or delete them if they are no longer needed:',
  INACTIVE_FORCED_UNVERIFIED:
    'one or more of the entities {ownerPhrase} have remained <strong>Unverified since they were manually set to Unverified</strong> and their <strong>Quality Seal has changed to Inactive</strong>. They are hidden from the Software Catalog by default and may be deleted after the applicable retention period. Please review and update these entities, or delete them if they are no longer needed:',
  INACTIVE_MANUAL:
    'one or more of the entities {ownerPhrase} have been manually marked <strong>Inactive</strong>. They are hidden from the Software Catalog by default and may be deleted after the applicable retention period. Please review and update these entities, or delete them if they are no longer needed:',
} as const;

/** Placeholder replaced with one of {@link NOTIFICATION_OWNER_PHRASES}. */
export const OWNER_PHRASE_PLACEHOLDER = '{ownerPhrase}';

/** Placeholder replaced with total days from last verification until Inactive. */
export const INACTIVE_DAYS_PLACEHOLDER = '{inactiveDays}';

export const NOTIFICATION_OWNER_PHRASES = {
  /** Group owner, e.g. "your team <strong>ACF Team</strong> owns". */
  group: (ownerTitle: string) =>
    `your team <strong>${ownerTitle}</strong> owns`,
  /** The recipient is the individual owner. */
  user: () => 'you own',
} as const;

export const NOTIFICATION_NOTES = {
  REVIEW_PENDING:
    'Note: Each entity will show the Quality Seal state <strong>Review Pending</strong> within the Software Catalog until you approve or update the entity.',
  REMINDER:
    'Note: Each entity will continue to show the Quality Seal state <strong>Review Pending</strong> in the Software Catalog until you approve or update the entity.',
  UNVERIFIED:
    'Note: Each entity will show the Quality Seal state <strong>Unverified</strong> in the Software Catalog until you update or approve the entity.',
  INACTIVE:
    'Note: Each entity will show the Quality Seal state <strong>Inactive</strong> until you update or approve it.',
} as const;
